import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material';
import { Logout } from '@mui/icons-material';
import { checkSession, logoutUser, resetUIState } from 'redux/slices/authSlice';

/**
 * The "View As" notice and its exit. Full-width in the one-level sidebar's
 * panel; `compact` is the icon-only form for the two-level sidebar's rail,
 * which is the only part of that sidebar still on screen when it collapses.
 */
const ViewAsBanner = ({ compact = false }) => {
  const dispatch = useDispatch();
  const isViewAsMode = useSelector((state) => state.auth?.isViewAsMode);

  if (!isViewAsMode) return null;

  const exitViewAs = () => {
    dispatch(logoutUser())
      .unwrap()
      .then(() => {
        dispatch(resetUIState());
        dispatch(checkSession());
      })
      .catch(() => {
        dispatch(resetUIState());
        dispatch(checkSession());
      });
    window.location.href = '/auth/signin';
  };

  if (compact) {
    return (
      <Tooltip title="View As mode: exit" arrow placement="right">
        <IconButton
          size="small"
          aria-label="Exit View As"
          onClick={exitViewAs}
          sx={{
            bgcolor: 'warning.main',
            color: 'warning.contrastText',
            '&:hover': { bgcolor: 'warning.dark' },
          }}
        >
          <Logout sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <Box sx={{ mt: 1 }}>
      <Box
        sx={{
          bgcolor: 'warning.main',
          color: 'warning.contrastText',
          p: 1.5,
          m: 1,
          mb: 0,
          borderRadius: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
        }}
      >
        <Typography variant="caption" sx={{ fontWeight: 700, mb: 1 }}>
          View As Mode
        </Typography>
        <Button
          size="small"
          variant="contained"
          color="inherit"
          onClick={exitViewAs}
          fullWidth
        >
          Exit View As
        </Button>
      </Box>
    </Box>
  );
};

ViewAsBanner.propTypes = {
  compact: PropTypes.bool,
};

export default ViewAsBanner;
